import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useBackButtonToPrevRoute } from '@/hooks/useBackButtonToPrevRoute.tsx';
import { routes } from '@/navigation/routes.tsx';
import { useProfileStore } from '@/store/useProfileStore.ts';
import Toggle from '@/components/common/Toggle/Toggle.tsx';
import { InputContent } from '@/components/common/InputContent/InputContent.tsx';
import {
  blockContainer,
  menuItemStyle,
  menuTextStyle,
  subtitleStyle
} from '@/pages/Profile/Settings/styles.ts';

interface INotificationSettings {
  new_likes: boolean;
  new_matches: boolean;
  new_messages: boolean;
}

const Notifications: FC = () => {
  useBackButtonToPrevRoute(routes.profileSettings, false);

  const { t } = useTranslation();
  const { aboutMe, updatePolicy } = useProfileStore();

  const groups = aboutMe?.channel_policy?.disabled_notification_groups;

  const [notificationSettings, setNotificationSettings] =
    useState<INotificationSettings>({
      new_likes: groups?.new_likes ?? false,
      new_matches: groups?.new_matches ?? false,
      new_messages: groups?.new_messages ?? false
    });

  const handleToggle = async (
    id: keyof INotificationSettings,
    checked: boolean
  ) => {
    const newValue = !checked;
    setNotificationSettings((prev) => ({
      ...prev,
      [id]: newValue
    }));

    try {
      await updatePolicy({
        disabled_notification_groups: {
          ...notificationSettings,
          [id]: newValue
        }
      });
    } catch (error) {
      console.error('Failed to update notification settings:', error);
    }
  };

  return (
    <div style={{ height: 'calc(100vh - 100px)', paddingTop: '100px' }}>
      <InputContent title={t('notifications_menu_item')}>
        <div style={blockContainer}>
          <div style={menuItemStyle}>
            <div style={{ flexGrow: 1, textAlign: 'left' }}>
              <span style={menuTextStyle}>
                {t('likes_notifications_switcher_title')}
              </span>
              <div style={subtitleStyle}>
                {t('likes_notifications_switcher_subtitle')}
              </div>
            </div>
            <Toggle
              isChecked={!notificationSettings.new_likes} // Invert: false means toggle is on
              onChange={(checked) => handleToggle('new_likes', checked)}
            />
          </div>
          <div style={menuItemStyle}>
            <div style={{ flexGrow: 1, textAlign: 'left' }}>
              <span style={menuTextStyle}>
                {t('matches_notifications_switcher_title')}
              </span>
              <div style={subtitleStyle}>
                {t('matches_notifications_switcher_subtitle')}
              </div>
            </div>
            <Toggle
              isChecked={!notificationSettings.new_matches}
              onChange={(checked) => handleToggle('new_matches', checked)}
            />
          </div>
          <div style={menuItemStyle}>
            <div style={{ flexGrow: 1, textAlign: 'left' }}>
              <span style={menuTextStyle}>
                {t('messages_notifications_switcher_title')}
              </span>
              <div style={subtitleStyle}>
                {t('messages_notifications_switcher_subtitle')}
              </div>
            </div>
            <Toggle
              isChecked={!notificationSettings.new_messages}
              onChange={(checked) => handleToggle('new_messages', checked)}
            />
          </div>
        </div>
      </InputContent>
    </div>
  );
};

export default Notifications;
